import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DemoMachine.css';
import { getDemoMachinesList } from '../../helper/DemoMachinesListHelper';
import { AssignHospital } from '../../helper/RequestCallbackHelper';
import { connect } from 'react-redux';
import moment from 'moment';
import { TIMELINE_CONST, ACTIVE_DEVICE } from '../../common/constant/common-constants';
import { ROLE } from '../../enum/role';
import CsvExportButton from '../../components/CsvExportButton/CsvExportButton';
import PageStickyChrome from '../../components/PageStickyChrome/PageStickyChrome';
import PageFilterSearch from '../../components/PageStickyChrome/PageFilterSearch';
import { PageStatIcon } from '../../components/PageStickyChrome/PageStatIcons';

const DEMO_STATUS_FILTER = {
  ALL: 'all',
  NOT_CONNECTED: 'not_connected',
  VENTILATION: 'ventilation',
  STANDBY: 'standby',
};

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const normalizeDemoStatus = (status) => String(status ?? '').toLowerCase().trim();

const isNotConnectedStatus = (status) => {
  const normalized = normalizeDemoStatus(status);
  return normalized.includes('not connected')
    || normalized.includes('not-connected')
    || normalized.includes('not connect')
    || normalized.includes('disconnected')
    || normalized === 'offline';
};

const isConnectedStatus = (status) => {
  if (isNotConnectedStatus(status)) return false;
  const normalized = normalizeDemoStatus(status);
  return normalized.includes('connected') || normalized === 'online' || normalized === 'active';
};

const isVentilationStatus = (status) => {
  const normalized = normalizeDemoStatus(status);
  return normalized.includes('ventilat');
};

const isStandbyStatus = (status) => {
  const normalized = normalizeDemoStatus(status);
  return normalized.includes('standby') || normalized.includes('stand by') || normalized.includes('idle');
};

const matchesStatusFilter = (item, filter) => {
  if (filter === DEMO_STATUS_FILTER.ALL) return true;
  if (filter === DEMO_STATUS_FILTER.NOT_CONNECTED) return isNotConnectedStatus(item.status);
  if (filter === DEMO_STATUS_FILTER.VENTILATION) return isVentilationStatus(item.status);
  if (filter === DEMO_STATUS_FILTER.STANDBY) return isStandbyStatus(item.status);
  return true;
};

const getStatusKind = (status) => {
  if (isVentilationStatus(status)) return 'ventilation';
  if (isStandbyStatus(status)) return 'standby';
  if (isNotConnectedStatus(status)) return 'not-connected';
  if (isConnectedStatus(status)) return 'connected';
  return 'neutral';
};

const formatSerialForExport = (serialNo) => {
  if (!serialNo) return '';
  const parts = serialNo.split('-').filter((part) => part.includes('1G'));
  return parts.length ? parts.join('-') : serialNo;
};

const getDisplaySerial = (serialNo) => {
  if (!serialNo) return '—';
  const parts = String(serialNo).split('-').filter((part) => part.includes('1G'));
  return parts.length ? parts.join('-') : serialNo;
};

const formatSyncUpToIst = (value) => {
  if (!value) return null;
  const parsed = moment.utc(value);
  if (!parsed.isValid()) return null;
  return parsed.utcOffset(330).format(TIMELINE_CONST.DATE_TIME_FORMAT);
};

const matchesSearch = (item, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  const fields = [
    item.SerialNo,
    getDisplaySerial(item.SerialNo),
    item.Location,
    item.status,
    item.Owner,
    item.osVersion,
    item.softwareVersion,
  ];
  return fields.some((field) => String(field ?? '').toLowerCase().includes(q));
};

const matchesOwnerFilter = (item, ownerFilter) => {
  if (!ownerFilter) return true;
  return String(item.Owner ?? '').trim() === ownerFilter;
};

const matchesVersionFilter = (item, versionFilter) => {
  if (!versionFilter) return true;
  const q = versionFilter.toLowerCase();
  const os = String(item.osVersion ?? '').toLowerCase();
  const software = String(item.softwareVersion ?? '').toLowerCase();
  return os.includes(q) || software.includes(q);
};

const DemoDeviceCard = ({ machine, onOpenCms }) => {
  const serial = getDisplaySerial(machine.SerialNo);
  const statusKind = getStatusKind(machine.status);
  const machineRunTime = displayValue(machine.totalMachineRunTime?.time);
  const ventilationRunTime = displayValue(machine.totalVentilationRuntime?.time);
  const machineSyncedAt = formatSyncUpToIst(machine.totalMachineRunTime?.sync_up_to);
  const ventilationSyncedAt = formatSyncUpToIst(machine.totalVentilationRuntime?.sync_up_to);

  return (
    <article
      className="demo-device-card"
      onClick={() => onOpenCms(machine.SerialNo)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenCms(machine.SerialNo);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="demo-device-card-head">
        <span className="demo-device-serial">{serial}</span>
        {machine.status && (
          <span className={`demo-device-status demo-device-status--${statusKind}`}>
            {machine.status}
          </span>
        )}
      </div>

      <div className="demo-device-details">
        <div className="demo-device-detail-row">
          <span className="demo-device-detail-label">SYSTEM</span>
          <span className="demo-device-detail-val">{displayValue(machine.softwareVersion)}</span>
        </div>
        <div className="demo-device-detail-row">
          <span className="demo-device-detail-label">OS</span>
          <span className="demo-device-detail-val">{displayValue(machine.osVersion)}</span>
        </div>
        <div className="demo-device-detail-row">
          <span className="demo-device-detail-label">LOCATION</span>
          <span className="demo-device-detail-val demo-device-detail-val--wrap">{displayValue(machine.Location)}</span>
        </div>
        <div className="demo-device-runtime">
          <span className="demo-device-detail-label">Machine Run Time</span>
          <span className="demo-device-detail-val">{machineRunTime}</span>
          <span className="demo-device-sync">
            {machineSyncedAt ? `Synced ${machineSyncedAt}` : '—'}
          </span>
        </div>
        <div className="demo-device-runtime">
          <span className="demo-device-detail-label">Ventilation Runtime</span>
          <span className="demo-device-detail-val">{ventilationRunTime}</span>
          <span className="demo-device-sync">
            {ventilationSyncedAt ? `Synced ${ventilationSyncedAt}` : '—'}
          </span>
        </div>
        <div className="demo-device-detail-row">
          <span className="demo-device-detail-label">OWNER</span>
          <span className="demo-device-detail-val">{displayValue(machine.Owner)}</span>
        </div>
      </div>
    </article>
  );
};

const OwnerFilterDropdown = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    searchRef.current?.focus();

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((owner) => owner.toLowerCase().includes(q));
  }, [options, query]);

  const selectOwner = (owner) => {
    onChange(owner);
    setQuery('');
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`demo-owner-dropdown${open ? ' demo-owner-dropdown--open' : ''}${value ? ' demo-owner-dropdown--active' : ''}`}
    >
      <button
        type="button"
        className="demo-owner-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by owner"
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span className="demo-owner-dropdown__meta">
          <span className="demo-owner-dropdown__label">Owner</span>
          <span className="demo-owner-dropdown__value">{value || 'All Owners'}</span>
        </span>
        <svg className="demo-owner-dropdown__chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5 7.5 10 12.5 15 7.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="demo-owner-dropdown__menu" role="listbox">
          <div className="demo-owner-dropdown__search">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search owners..."
              aria-label="Search owners"
            />
          </div>
          <button
            type="button"
            role="option"
            aria-selected={!value}
            className={`demo-owner-dropdown__option${!value ? ' demo-owner-dropdown__option--selected' : ''}`}
            onClick={() => selectOwner('')}
          >
            All Owners
          </button>
          {filteredOptions.length === 0 ? (
            <div className="demo-owner-dropdown__empty">No owners match</div>
          ) : (
            filteredOptions.map((owner) => (
              <button
                key={owner}
                type="button"
                role="option"
                aria-selected={value === owner}
                className={`demo-owner-dropdown__option${value === owner ? ' demo-owner-dropdown__option--selected' : ''}`}
                onClick={() => selectOwner(owner)}
              >
                {owner}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const DemoMachine = (props) => {
  const [dataContainer, setDataContainer] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [versionFilter, setVersionFilter] = useState('');
  const [runTimeQuery, setRunTimeQuery] = useState(10);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(DEMO_STATUS_FILTER.ALL);
  const [isRuntimeModalOpen, setIsRuntimeModalOpen] = useState(false);
  const [draftDays, setDraftDays] = useState('10');
  const [daysError, setDaysError] = useState('');
  const searchInputRef = useRef(null);
  const daysInputRef = useRef(null);
  const runtimePopoverRef = useRef(null);

  const handleDemoMachine = useCallback((time) => {
    setLoading(true);
    getDemoMachinesList(time)
      .then((res) => setDataContainer(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    handleDemoMachine(10);
  }, [handleDemoMachine]);

  useEffect(() => {
    setStatusFilter(DEMO_STATUS_FILTER.ALL);
  }, [searchQuery, ownerFilter, versionFilter, runTimeQuery]);

  useEffect(() => {
    if (!isRuntimeModalOpen) return undefined;

    daysInputRef.current?.focus();
    daysInputRef.current?.select();

    const handlePointerDown = (event) => {
      if (!runtimePopoverRef.current?.contains(event.target)) {
        setIsRuntimeModalOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsRuntimeModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRuntimeModalOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openRuntimeModal = () => {
    if (isRuntimeModalOpen) {
      setIsRuntimeModalOpen(false);
      return;
    }
    setDraftDays(String(runTimeQuery));
    setDaysError('');
    setIsRuntimeModalOpen(true);
  };

  const handleApplyRuntimeDays = () => {
    const days = Number(draftDays);
    if (!Number.isInteger(days) || days < 1) {
      setDaysError('Enter a whole number of 1 or more.');
      return;
    }

    setDaysError('');
    setIsRuntimeModalOpen(false);
    if (days === runTimeQuery) return;
    setRunTimeQuery(days);
    handleDemoMachine(days);
  };

  const openCMS = (machineUDI) => {
    const email = props.email;
    if (!email) return;

    AssignHospital({
      machineUDI,
      email,
      activeDevice: ACTIVE_DEVICE.V730i,
    }).then((res) => {
      if (res === 'Hospital ID updated successfully.') {
        window.open(process.env.REACT_APP_CMS_APP_URL, '_blank');
      } else {
        alert(res);
      }
    });
  };

  const ownerOptions = useMemo(() => {
    const owners = new Set();
    dataContainer.forEach((item) => {
      const owner = String(item.Owner ?? '').trim();
      if (owner) owners.add(owner);
    });
    return [...owners].sort((a, b) => a.localeCompare(b));
  }, [dataContainer]);

  const baseFilteredMachines = useMemo(
    () => dataContainer.filter((item) => (
      matchesSearch(item, searchQuery)
      && matchesOwnerFilter(item, ownerFilter)
      && matchesVersionFilter(item, versionFilter)
    )),
    [dataContainer, searchQuery, ownerFilter, versionFilter]
  );

  const filteredMachines = useMemo(
    () => baseFilteredMachines.filter((item) => matchesStatusFilter(item, statusFilter)),
    [baseFilteredMachines, statusFilter]
  );

  const stats = useMemo(() => ({
    total: baseFilteredMachines.length,
    notConnected: baseFilteredMachines.filter((item) => isNotConnectedStatus(item.status)).length,
    ventilation: baseFilteredMachines.filter((item) => isVentilationStatus(item.status)).length,
    standby: baseFilteredMachines.filter((item) => isStandbyStatus(item.status)).length,
  }), [baseFilteredMachines]);

  const getExportData = () => filteredMachines;

  const getExportColumns = () => [
    {
      header: 'Machine Serial No.',
      accessor: (row) => formatSerialForExport(row.SerialNo),
    },
    {
      header: 'OS Version',
      accessor: (row) => row.osVersion ?? '-',
    },
    {
      header: 'Software Version',
      accessor: (row) => row.softwareVersion ?? '-',
    },
    {
      header: 'Location',
      accessor: (row) => row.Location ?? '-',
    },
    {
      header: 'Total Machine Run Time',
      accessor: (row) => displayValue(row.totalMachineRunTime?.time),
    },
    {
      header: 'Machine Run Sync Up To (IST)',
      accessor: (row) => formatSyncUpToIst(row.totalMachineRunTime?.sync_up_to) || '-',
    },
    {
      header: 'Total Ventilation Runtime',
      accessor: (row) => displayValue(row.totalVentilationRuntime?.time),
    },
    {
      header: 'Ventilation Sync Up To (IST)',
      accessor: (row) => formatSyncUpToIst(row.totalVentilationRuntime?.sync_up_to) || '-',
    },
    {
      header: 'Current Status',
      accessor: (row) => row.status ?? '-',
    },
    {
      header: 'Owner',
      accessor: (row) => row.Owner ?? '-',
    },
  ];

  if (!(props.userRole === ROLE.ADMIN || props.userRole === ROLE.SERVICE || props.userRole === ROLE.PRODUCTION)) {
    return (
      <div>
        <h1>Unauthorized Access</h1>
        <p>You do not have the necessary permissions to access this page.</p>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <PageStickyChrome>
      <div className="demo-header">
        <h3 className="demo-title">Demo Machines</h3>
      </div>

      <div className="demo-filters-bar">
        <PageFilterSearch inputRef={searchInputRef}>
          <input
            ref={searchInputRef}
            type="text"
            className="demo-search-input"
            placeholder="Search serial, location..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </PageFilterSearch>
        <OwnerFilterDropdown
          value={ownerFilter}
          options={ownerOptions}
          onChange={setOwnerFilter}
        />
        <input
          type="text"
          className="demo-filter-input"
          placeholder="Filter version (OS or System)..."
          value={versionFilter}
          onChange={(event) => setVersionFilter(event.target.value)}
          aria-label="Filter by version"
        />
        <div
          ref={runtimePopoverRef}
          className={`demo-runtime-popover${isRuntimeModalOpen ? ' demo-runtime-popover--open' : ''}`}
        >
          <button
            type="button"
            className="demo-filter-group demo-runtime-control"
            onClick={openRuntimeModal}
            aria-haspopup="dialog"
            aria-expanded={isRuntimeModalOpen}
            aria-label={`Change run time, currently last ${runTimeQuery} days`}
          >
            <span className="demo-filter-group-label">Last</span>
            <span className="demo-runtime-value">{runTimeQuery}</span>
            <span className="demo-filter-group-suffix">Days</span>
          </button>

          {isRuntimeModalOpen && (
            <div className="demo-runtime-popover__menu" role="dialog" aria-labelledby="demo-runtime-title">
              <p id="demo-runtime-title" className="demo-runtime-popover__title">Last days</p>
              <div className="demo-runtime-popover__input-row">
                <input
                  id="demo-runtime-days"
                  ref={daysInputRef}
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={draftDays}
                  onWheel={() => daysInputRef.current?.blur()}
                  onChange={(event) => {
                    const value = event.target.value.replace(/[^\d]/g, '');
                    setDraftDays(value);
                    if (daysError) setDaysError('');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') handleApplyRuntimeDays();
                  }}
                  className="demo-runtime-popover__input"
                  placeholder="Days"
                />
                <span className="demo-runtime-popover__suffix">Days</span>
              </div>
              {daysError && <p className="demo-runtime-popover__error">{daysError}</p>}
              <div className="demo-runtime-popover__actions">
                <button type="button" onClick={() => setIsRuntimeModalOpen(false)}>
                  Cancel
                </button>
                <button type="button" onClick={handleApplyRuntimeDays}>
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
        <CsvExportButton
          getData={getExportData}
          columns={getExportColumns()}
          fileName={`demo-machines-${runTimeQuery}days`}
          disabled={loading}
          className="demo-export-btn"
          title="Export to Excel"
        />
      </div>

      <div className="demo-stats-grid">
        <button
          type="button"
          className={`demo-stat-card demo-stat-card--total${statusFilter === DEMO_STATUS_FILTER.ALL ? ' demo-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(DEMO_STATUS_FILTER.ALL)}
          aria-pressed={statusFilter === DEMO_STATUS_FILTER.ALL}
        >
          <PageStatIcon name="total" />
          <span className="demo-stat-label">TOTAL MACHINES</span>
          <span className="demo-stat-value">{stats.total}</span>
        </button>
        <button
          type="button"
          className={`demo-stat-card demo-stat-card--not-connected${statusFilter === DEMO_STATUS_FILTER.NOT_CONNECTED ? ' demo-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(DEMO_STATUS_FILTER.NOT_CONNECTED)}
          aria-pressed={statusFilter === DEMO_STATUS_FILTER.NOT_CONNECTED}
        >
          <PageStatIcon name="offline" />
          <span className="demo-stat-label">NOT CONNECTED</span>
          <span className="demo-stat-value">{stats.notConnected}</span>
        </button>
        <button
          type="button"
          className={`demo-stat-card demo-stat-card--ventilation${statusFilter === DEMO_STATUS_FILTER.VENTILATION ? ' demo-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(DEMO_STATUS_FILTER.VENTILATION)}
          aria-pressed={statusFilter === DEMO_STATUS_FILTER.VENTILATION}
        >
          <PageStatIcon name="ventilation" />
          <span className="demo-stat-label">VENTILATION</span>
          <span className="demo-stat-value">{stats.ventilation}</span>
        </button>
        <button
          type="button"
          className={`demo-stat-card demo-stat-card--standby${statusFilter === DEMO_STATUS_FILTER.STANDBY ? ' demo-stat-card--active' : ''}`}
          onClick={() => setStatusFilter(DEMO_STATUS_FILTER.STANDBY)}
          aria-pressed={statusFilter === DEMO_STATUS_FILTER.STANDBY}
        >
          <PageStatIcon name="standby" />
          <span className="demo-stat-label">STANDBY</span>
          <span className="demo-stat-value">{stats.standby}</span>
        </button>
      </div>

      <div className="demo-toolbar">
        <h2 className="demo-section-title">Machines</h2>
      </div>
      </PageStickyChrome>

      <div className="demo-cards-scroll">
        {loading ? (
          <div className="demo-loading" aria-busy="true" aria-live="polite">
            <div className="demo-spinner" aria-label="Loading demo machines" />
            <p>Loading demo machines…</p>
          </div>
        ) : filteredMachines.length === 0 ? (
          <div className="demo-empty">No demo machines found.</div>
        ) : (
          <div className="demo-cards-grid">
            {filteredMachines.map((machine) => (
              <DemoDeviceCard
                key={machine.SerialNo || `${machine.osVersion}-${machine.updatedAt}`}
                machine={machine}
                onOpenCms={openCMS}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  email: state.userDetails?.email,
  userRole: state.userDetails.userRole,
});

export default connect(mapStateToProps, null)(DemoMachine);
